import {
  Box,
  Col,
  Form,
  Row,
  SelectField,
  SubmitButton,
  TextAreaInputField,
  theme,
} from '@/components';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

import { ApprovalDecisionFormSchema } from '@/features/workflow/models/approval';

type FormValues = z.infer<typeof ApprovalDecisionFormSchema>;

type Props = {
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
};

export const ApprovalDecisionForm: FC<Props> = ({
  initialValues,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const {
    handleSubmit,
    watch,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(ApprovalDecisionFormSchema),
  });

  const [reviewType, comment] = watch(['type', 'comment']);

  const options = [
    {
      label: t('approve'),
      value: 'approve',
    },
    {
      label: t('reject'),
      value: 'reject',
    },
  ];

  return (
    <Box backgroundColor={token.colorBgLayout} padding={8}>
      <Form
        leavePageAlertCondition={{
          isDirty,
          isSubmitting,
          isSubmitSuccessful,
        }}
        layout="vertical"
        onFinish={handleSubmit(onSubmit)}
      >
        <TextAreaInputField
          name="comment"
          label={t('message-review-comment')}
          control={control}
          errors={errors}
          rows={3}
          style={{ marginBottom: 8 }}
          required
        />

        <Row gutter={[8, 0]} wrap={false}>
          <Col flex="auto">
            <SelectField
              name="type"
              control={control}
              options={options}
              errors={errors}
              required
              wrapperStyle={{ marginBottom: 0 }}
            />
          </Col>
          <Col flex="none">
            <SubmitButton
              text={t('submit')}
              loading={isSubmitting}
              disabled={!reviewType || !comment}
            />
          </Col>
        </Row>
      </Form>
    </Box>
  );
};
